import Chainable from "@repo/utils/Chainable";
import UNumber from "@repo/utils/UNumber";

namespace Animation {
  // Any rAFs queued in your event handlers will be executed in the same frame.
  // Any rAFs queued in a rAF will be executed in the next frame.
  export const nextFrame = (fn?: () => void) =>
    new Promise<void>((resolve) => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          fn?.();
          resolve();
        });
      });
    });

  export type EaseFn = (t: number) => number;

  type Ease = { [key: string]: EaseFn | ((...args: any[]) => EaseFn) | Ease };
  export const ease = {
    sine: {
      out: (t: number) => Math.sin((t * Math.PI) / 2),
    },
  } satisfies Ease;

  export const cancel = Symbol("Animation.cancel");

  export type Tween = ReturnType<typeof tween>;
  export const tween = Chainable.create(
    Chainable.wrapFactory(
      ({ from = 0, to = 1, easeFn = ease.sine.out }) =>
        (durationMs: number, onFrame: (value: number) => void) => {
          let cancel = false;

          return Object.assign(
            new Promise<void>((resolve, reject) => {
              const startTime = performance.now();

              (function nextFrame(t = startTime) {
                if (cancel) return reject(Animation.cancel);

                const progress = easeFn(
                  UNumber.progress(t, [startTime, startTime + durationMs])
                );

                onFrame(
                  from === 0 && to === 1
                    ? progress
                    : UNumber.progress.toValue(progress, [from, to])
                );

                if (progress < 1) requestAnimationFrame(nextFrame);
                else resolve();
              })();
            }),
            {
              cancel() {
                cancel = true;
              },
            }
          );
        }
    ),
    {
      ease: (easeFn) => ({
        easeFn,
      }),
      fromTo: (from, to) => ({
        from,
        to,
      }),
    }
  );
}

export default Animation;
